/**
 * Follow logic for related and upward navigation
 */

import type { SelectElement, WidgetState } from './types';
import type { Subdivision } from '@countriesdb/widget-core';
import { dispatchUpdateEvent } from './event-system';
import { CountriesDBClient } from '@countriesdb/widget-core';
import { updateSubdivisionSelect } from './initialization';

/**
 * Trigger follow logic when a subdivision is selected
 */
export async function triggerFollowLogic(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  followRelated: boolean,
  followUpward: boolean,
  updateSubdivisionSelectFn: (
    select: SelectElement,
    apiKey: string,
    countryCode: string
  ) => Promise<void>,
  updateSubdivisionsFn?: (
    countrySelect: SelectElement,
    apiKey: string
  ) => Promise<void>
): Promise<void> {
  if (!followRelated && !followUpward) {
    return;
  }

  const linkedCountrySelect = document.querySelector<SelectElement>(
    `.country-selection[data-name="${select.dataset.country}"]`
  );
  
  // Only work if country is single-select (never when country is multi)
  if (!linkedCountrySelect || linkedCountrySelect.multiple) {
    return;
  }

  const allSubs = state.subdivisionsMap.get(select);
  if (!allSubs) {
    return;
  }

  // For follow_upward, only work if subdivision is single-select
  if (followUpward && select.multiple) {
    return;
  }

  const selectedCode = select.value;
  if (!selectedCode) {
    return;
  }

  const picked = allSubs.find((s) => s.code === selectedCode);
  if (!picked) {
    return;
  }

  // follow_related
  if (followRelated && picked.related_country_code) {
    const targetCountry = picked.related_country_code;
    const targetSubdivision = picked.related_subdivision_code;

    const relatedSubsSelect = document.querySelector<SelectElement>(
      `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
    );
    
    if (relatedSubsSelect && targetSubdivision) {
      relatedSubsSelect.dataset._widgetTempPreselect = targetSubdivision; // one-time preselect
    }

    if (linkedCountrySelect.value !== targetCountry) {
      // Directly set value like old widget did
      linkedCountrySelect.value = targetCountry;
      dispatchUpdateEvent(linkedCountrySelect, { type: 'country', reason: 'regular' });
      // Update all subdivision selects for the new country (like old widget)
      if (updateSubdivisionsFn) {
        await updateSubdivisionsFn(linkedCountrySelect, apiKey);
      } else if (relatedSubsSelect) {
        // Fallback: update just the one subdivision select
        await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, targetCountry);
      }
    } else {
      if (relatedSubsSelect && targetSubdivision) {
        await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, targetCountry);
      }
    }
  }

  // follow_upward
  if (followUpward && picked.is_subdivision_of) {
    const parentCode = picked.is_subdivision_of.parent_country_code;
    const parentSub = picked.is_subdivision_of.subdivision_code;

    if (parentSub) {
      const relatedSubsSelect = document.querySelector<SelectElement>(
        `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
      );
      // Only preselect if subdivision is single-select (follow_upward doesn't work with multi)
      if (relatedSubsSelect && !relatedSubsSelect.multiple) {
        relatedSubsSelect.dataset._widgetTempPreselect = parentSub; // one-time preselect
      }
    }

          if (linkedCountrySelect.value !== parentCode) {
            // Directly set value like old widget did
            linkedCountrySelect.value = parentCode;
            dispatchUpdateEvent(linkedCountrySelect, { type: 'country', reason: 'regular' });
            // Update all subdivision selects for the new country (like old widget)
            if (updateSubdivisionsFn) {
              await updateSubdivisionsFn(linkedCountrySelect, apiKey);
            } else {
              const relatedSubsSelect = document.querySelector<SelectElement>(
                `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
              );
              if (relatedSubsSelect) {
                await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, parentCode);
              }
            }
          } else if (parentSub) {
      const relatedSubsSelect = document.querySelector<SelectElement>(
        `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
      );
      if (relatedSubsSelect && !relatedSubsSelect.multiple) {
        await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, parentCode);
      }
    }
  }
}

/**
 * Handle follow_related from subdivision change event
 */
export async function handleFollowRelatedFromSubdivision(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  followRelated: boolean,
  updateSubdivisionSelectFn: (
    select: SelectElement,
    apiKey: string,
    countryCode: string
  ) => Promise<void>
): Promise<void> {
  if (!followRelated) {
    return;
  }

  const linkedCountrySelect = document.querySelector<SelectElement>(
    `.country-selection[data-name="${select.dataset.country}"]`
  );
  
  // Only work if country is single-select (never when country is multi)
  if (!linkedCountrySelect || linkedCountrySelect.multiple) {
    return;
  }

  const allSubs = state.subdivisionsMap.get(select);
  if (!allSubs) {
    return;
  }

  // Get selected code (for multi-select, use first selected value)
  const selectedCode = select.multiple
    ? (select.selectedOptions[0]?.value || null)
    : select.value;
    
  if (!selectedCode) {
    return;
  }

  const picked = allSubs.find((s) => s.code === selectedCode);
  if (!picked || !picked.related_country_code) {
    return;
  }

  const targetCountry = picked.related_country_code;
  const targetSubdivision = picked.related_subdivision_code;

  const relatedSubsSelect = document.querySelector<SelectElement>(
    `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
  );
  
  if (relatedSubsSelect && targetSubdivision) {
    relatedSubsSelect.dataset.preselected = targetSubdivision;
  }

  if (linkedCountrySelect.value !== targetCountry) {
    // Directly set value like old widget did
    linkedCountrySelect.value = targetCountry;
    dispatchUpdateEvent(linkedCountrySelect, { type: 'country', reason: 'regular' });
    // Update all subdivision selects for the new country (like old widget)
    // Note: updateSubdivisionsFn is not available in handleFollowRelatedFromSubdivision,
    // so we update the subdivision select manually
    if (relatedSubsSelect) {
      await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, targetCountry);
    }
  } else {
    if (relatedSubsSelect && targetSubdivision) {
      await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, targetCountry);
    }
  }
}

/**
 * Handle follow_upward from subdivision change event
 */
export async function handleFollowUpwardFromSubdivision(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  followUpward: boolean,
  updateSubdivisionSelectFn: (
    select: SelectElement,
    apiKey: string,
    countryCode: string
  ) => Promise<void>
): Promise<void> {
  if (!followUpward) {
    return;
  }
  
  // Disable for multi-select subdivisions
  if (select.multiple) {
    return;
  }

  const linkedCountrySelect = document.querySelector<SelectElement>(
    `.country-selection[data-name="${select.dataset.country}"]`
  );
  
  // Only work if country is single-select (never when country is multi)
  if (!linkedCountrySelect || linkedCountrySelect.multiple) {
    return;
  }

  const allSubs = state.subdivisionsMap.get(select);
  if (!allSubs) {
    return;
  }

  const selectedCode = select.value;
  if (!selectedCode) {
    return;
  }

  const picked = allSubs.find((s) => s.code === selectedCode);
  if (!picked || !picked.is_subdivision_of) {
    return;
  }

  const parentCode = picked.is_subdivision_of.parent_country_code;
  const parentSub = picked.is_subdivision_of.subdivision_code;

  if (parentSub) {
    const relatedSubsSelect = document.querySelector<SelectElement>(
      `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
    );
    if (relatedSubsSelect) {
      relatedSubsSelect.dataset.preselected = parentSub;
    }
  }

    if (linkedCountrySelect.value !== parentCode) {
      // Directly set value like old widget did
      linkedCountrySelect.value = parentCode;
      dispatchUpdateEvent(linkedCountrySelect, { type: 'country', reason: 'follow' });
      // Update all subdivision selects for the new country (like old widget)
      // Note: updateSubdivisionsFn is not available in handleFollowUpwardFromSubdivision,
      // so we update the subdivision select manually
      const relatedSubsSelect = document.querySelector<SelectElement>(
        `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
      );
      if (relatedSubsSelect) {
        await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, parentCode);
      }
    } else if (parentSub) {
    const relatedSubsSelect = document.querySelector<SelectElement>(
      `.subdivision-selection[data-country="${linkedCountrySelect.dataset.name}"]`
    );
    if (relatedSubsSelect) {
      await updateSubdivisionSelectFn(relatedSubsSelect, apiKey, parentCode);
    }
  }
}

/**
 * Handle follow_upward from country change event
 */
export async function handleFollowUpwardFromCountry(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  followUpward: boolean,
  updateSubdivisionSelectFn: (
    select: SelectElement,
    apiKey: string,
    countryCode: string
  ) => Promise<void>
): Promise<void> {
  // Only works when country is single-select (never for multi-select)
  if (!followUpward || select.multiple) {
    return;
  }

  const countries = state.countriesMap.get(select);
  if (!countries) {
    return;
  }

  const chosen = select.value;
  if (!chosen) {
    return;
  }

  const picked = countries.find((c) => c.iso_alpha_2 === chosen);
  if (!picked || !picked.is_subdivision_of) {
    return;
  }

  const parentCode = picked.is_subdivision_of.parent_country_code;
  const parentSub = picked.is_subdivision_of.subdivision_code;

  if (select.value !== parentCode) {
    if (parentSub) {
      const linkedSubdivisionSelects = Array.from(
        document.querySelectorAll<SelectElement>(
          `.subdivision-selection[data-country="${select.dataset.name}"]`
        )
      );
      for (const s of linkedSubdivisionSelects) {
        // Only preselect if subdivision is single-select (follow_upward doesn't work with multi)
        if (!s.multiple) {
          s.dataset.preselected = parentSub;
        }
      }
    }
    // Directly set value like old widget did
    select.value = parentCode;
    dispatchUpdateEvent(select, { type: 'country', reason: 'regular' });
    // Update all subdivision selects for the new country (like old widget)
    // Note: updateSubdivisionsFn is not available in handleFollowUpwardFromCountry,
    // so we update all subdivision selects manually
    const linkedSubdivisionSelects = Array.from(
      document.querySelectorAll<SelectElement>(
        `.subdivision-selection[data-country="${select.dataset.name}"]`
      )
    );
    for (const s of linkedSubdivisionSelects) {
      await updateSubdivisionSelectFn(s, apiKey, parentCode);
    }
  } else if (parentSub) {
    const linkedSubdivisionSelects = Array.from(
      document.querySelectorAll<SelectElement>(
        `.subdivision-selection[data-country="${select.dataset.name}"]`
      )
    );
    for (const s of linkedSubdivisionSelects) {
      // Only update if subdivision is single-select
      if (!s.multiple) {
        await updateSubdivisionSelectFn(s, apiKey, parentCode);
      }
    }
  }
}

